import io from "socket.io-client";
import { SOCKET_URL } from "../config/apiConfig";

// Use centralized socket URL configuration

// List of all available socket events - centralized for consistency
const EVENTS = {
  // Connection events
  JOIN_DRIVER_ROOM: "joinDriverRoom",
  JOIN_USER_ROOM: "joinUserRoom",
  JOIN_ADMIN_ROOM: "joinAdminRoom",
  JOIN_VENDOR_ROOM: "joinVendorRoom",
  JOIN_CORPORATE_ROOM: "joinCorporateRoom",
  JOIN_CORPORATE_MEMBER_ROOM: "joinCorporateMemberRoom",

  // Wallet and commission events
  WALLET_UPDATED: "walletUpdated",
  COMMISSION_PROCESSED: "commissionProcessed",
  WITHDRAWAL_UPDATED: "withdrawalUpdated",

  // Booking lifecycle events
  DRIVER_ASSIGNED: "driverAssigned",
  DRIVER_REMOVED: "driver-removed",
  BOOKING_CREATED: "bookingCreated",
  BOOKING_UNASSIGNED: "booking-unassigned",
  BOOKING_UPDATED: "bookingUpdated",
  BOOKING_STATUS_CHANGED: "bookingStatusChanged",
  NEW_RIDE_REQUEST: "NEW_RIDE_REQUEST",
  RIDE_TAKEN: "RIDE_TAKEN",
  AUTO_ASSIGN_EXPIRED: "AUTO_ASSIGN_EXPIRED",
  RIDE_CANCELLED: "rideCancelled",
  RIDE_STARTED: "rideStarted",
  RIDE_COMPLETED: "rideCompleted",

  // Payment and refund events
  PAYMENT_RECEIVED: "paymentReceived",
  REFUND_INITIATED: "refundInitiated",
  REFUND_PROCESSED: "refundProcessed",
  REFUND_FAILED: "refundFailed",
  WALLET_PAYMENT_OTP: "wallet_payment_otp",

  // Rating events
  RATING_REQUESTED: "ratingRequested",
  RATING_SUBMITTED: "ratingSubmitted",
  DRIVER_RATING_UPDATE: "driverRatingUpdate",

  // Driver location events
  DRIVER_LOCATION_UPDATED: "driverLocationUpdated",

  // Live ride tracking
  JOIN_RIDE_ROOM: "joinRideRoom",
  LOCATION_UPDATE: "locationUpdate",

  // Driver approaching pickup (assigned phase)
  DRIVER_APPROACHING: "driverApproaching",
};

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.events = EVENTS;
    this._disabled = false;
    // Track joined rooms so we can automatically rejoin them upon connection or reconnection
    this._joinedRooms = {
      admin: false,
      user: null,
      driver: null,
      vendor: null,
      corporate: null,
      corporateMember: null,
      ride: null
    };
  }

  // Initialize the socket connection
  connect() {
    this._disabled = false;
    this._connectionAttempts = 0;

    // Prevent attempts from unsupported origins (remote server CORS)
    const allowedOrigins = new Set([
      'http://localhost:3000',
      'https://cabzigo-app.vercel.app',
      'https://cabzigo.rajeshwarinfotech.com'
    ]);
    const currentOrigin = window.location.origin;
    if (!allowedOrigins.has(currentOrigin) && SOCKET_URL.includes('rajeshwarinfotech.com')) {
      console.warn(`SocketService: origin ${currentOrigin} is not in the allowlist but will attempt connection.`);
    }
    // If socket exists, return it (socket.io handles reconnection automatically)
    if (this.socket) {
      if (this.socket.disconnected) {
        console.log("SocketService: Socket exists but is disconnected. Reconnecting...");
        this.socket.connect();
      }
      return this.socket;
    }

    const isProduction = window.location.hostname === 'cabzigo.com' || window.location.hostname === 'www.cabzigo.com';

    console.log(`SocketService: Initializing socket connection to: ${SOCKET_URL}`);

    this.socket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      upgrade: true,
      forceNew: true,
      withCredentials: true,
      secure: isProduction || SOCKET_URL.startsWith('https')
    });

    // Connection successful
    this.socket.on("connect", () => {
      this.isConnected = true;
      this._isConnecting = false;
      this._connectionAttempts = 0;
      this._retryCount = 0;
      this._eventRetryCounts = {}; // Reset per-event retry counts on successful connect
      this._hasWarnedDisconnected = false;

      // Rejoin any previously joined rooms automatically
      this.rejoinRooms();

      try {
        const transport = this.socket && this.socket.io && this.socket.io.engine && this.socket.io.engine.transport
          ? this.socket.io.engine.transport.name
          : 'unknown';
        console.log(`SocketService: connected to ${SOCKET_URL} (id=${this.socket.id}, transport=${transport})`);
      } catch (e) {
        console.log('SocketService: connected (transport unknown)');
      }
    });

    // Handle disconnection
    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;
      this._hasWarnedDisconnected = false;
      console.warn(`SocketService: disconnected (reason=${reason})`);
    });

    // Handle connection errors
    this.socket.on("connect_error", (error) => {
      this._connectionAttempts = (this._connectionAttempts || 0) + 1;
      console.error(`SocketService: connect_error (attempt ${this._connectionAttempts}):`, error instanceof Error ? error : error);
      this.isConnected = false;
      this._isConnecting = false;
      // Do NOT disable the socket permanently. Let Socket.IO built-in reconnection handle it.
    });

    // Additional helpful debug events
    this.socket.on('connect_timeout', (timeout) => {
      console.warn('SocketService: connect_timeout', timeout);
    });

    this.socket.on('reconnect_attempt', (attempt) => {
      console.log('SocketService: reconnect_attempt', attempt);
    });

    this.socket.on('reconnect', (attempt) => {
      console.log('SocketService: reconnect successful on attempt', attempt);
    });

    this.socket.on('reconnect_error', (err) => {
      console.error('SocketService: reconnect_error', err);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('SocketService: reconnect_failed');
    });

    this.socket.on('ping', () => {
      // keep light log to avoid noise
    });

    this.socket.on('pong', (latency) => {
      console.log('SocketService: pong (latency_ms)=', latency);
    });

    return this.socket;
  }

  // Rejoin previously joined rooms upon connection or reconnection
  rejoinRooms() {
    if (!this.socket || !this.isConnected) return;

    if (this._joinedRooms.admin) {
      console.log('SocketService: Rejoining admin room');
      this.socket.emit(EVENTS.JOIN_ADMIN_ROOM);
    }
    if (this._joinedRooms.user) {
      console.log('SocketService: Rejoining user room', this._joinedRooms.user);
      this.socket.emit(EVENTS.JOIN_USER_ROOM, this._joinedRooms.user);
    }
    if (this._joinedRooms.driver) {
      console.log('SocketService: Rejoining driver room', this._joinedRooms.driver);
      this.socket.emit(EVENTS.JOIN_DRIVER_ROOM, this._joinedRooms.driver);
    }
    if (this._joinedRooms.vendor) {
      console.log('SocketService: Rejoining vendor room', this._joinedRooms.vendor);
      this.socket.emit(EVENTS.JOIN_VENDOR_ROOM, this._joinedRooms.vendor);
    }
    if (this._joinedRooms.corporate) {
      console.log('SocketService: Rejoining corporate room', this._joinedRooms.corporate);
      this.socket.emit(EVENTS.JOIN_CORPORATE_ROOM, this._joinedRooms.corporate);
    }
    if (this._joinedRooms.corporateMember) {
      console.log('SocketService: Rejoining corporate member room', this._joinedRooms.corporateMember);
      this.socket.emit(EVENTS.JOIN_CORPORATE_MEMBER_ROOM, this._joinedRooms.corporateMember);
    }
    if (this._joinedRooms.ride) {
      console.log('SocketService: Rejoining ride room', this._joinedRooms.ride);
      this.socket.emit(EVENTS.JOIN_RIDE_ROOM, this._joinedRooms.ride);
    }
  }

  // Join a room as a driver
  joinDriverRoom(driverId) {
    this._joinedRooms.driver = driverId;
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinDriverRoom called but socket is not connected. Saved room for auto-rejoin.', { driverId });
      return;
    }

    console.log('SocketService: emitting joinDriverRoom', { driverId });
    this.socket.emit(EVENTS.JOIN_DRIVER_ROOM, driverId);
  }

  // Join a room as a user
  joinUserRoom(userId) {
    this._joinedRooms.user = userId;
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinUserRoom called but socket is not connected. Saved room for auto-rejoin.', { userId });
      return;
    }

    console.log('SocketService: emitting joinUserRoom', { userId });
    this.socket.emit(EVENTS.JOIN_USER_ROOM, userId);
  }

  // Join the admin room
  joinAdminRoom() {
    this._joinedRooms.admin = true;
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinAdminRoom called but socket is not connected. Saved room for auto-rejoin.');
      return;
    }

    console.log('SocketService: emitting joinAdminRoom');
    this.socket.emit(EVENTS.JOIN_ADMIN_ROOM);
  }

  // Join a room as a vendor
  joinVendorRoom(vendorId) {
    this._joinedRooms.vendor = vendorId;
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinVendorRoom called but socket is not connected. Saved room for auto-rejoin.', { vendorId });
      return;
    }

    console.log('SocketService: emitting joinVendorRoom', { vendorId });
    this.socket.emit(EVENTS.JOIN_VENDOR_ROOM, vendorId);
  }

  // Join a room as a corporate member (for corporate staff to receive booking updates)
  joinCorporateMemberRoom(corporateMemberId) {
    this._joinedRooms.corporateMember = corporateMemberId;
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinCorporateMemberRoom called but socket is not connected. Saved room for auto-rejoin.', { corporateMemberId });
      return;
    }

    console.log('SocketService: emitting joinCorporateMemberRoom', { corporateMemberId });
    this.socket.emit(EVENTS.JOIN_CORPORATE_MEMBER_ROOM, corporateMemberId);
  }

  // Join a room as a corporate account
  joinCorporateRoom(accountId) {
    this._joinedRooms.corporate = accountId;
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinCorporateRoom called but socket is not connected. Saved room for auto-rejoin.', { accountId });
      return;
    }

    console.log('SocketService: emitting joinCorporateRoom', { accountId });
    this.socket.emit(EVENTS.JOIN_CORPORATE_ROOM, accountId);
  }

  /**
   * Generic method to listen for any socket event
   * @param {string} eventName - Event name from EVENTS object
   * @param {Function} callback - Callback function to handle the event
   */
  listenForEvent(eventName, callback) {
    if (this._disabled) {
      // Silently ignore when sockets are disabled
      return () => {};
    }

    // Try to connect if socket doesn't exist
    if (!this.socket) {
      this.connect();
    }

    if (!this.socket) {
      console.error("Socket could not be initialized");
      return () => {};
    }

    if (!Object.values(EVENTS).includes(eventName)) {
      console.error(`Unknown event: ${eventName}`);
      return () => {};
    }

    // Add the listener directly using the callback
    this.socket.on(eventName, callback);

    return () => {
      // Provide a cleanup function that removes ONLY this callback by reference
      if (this.socket) {
        this.socket.off(eventName, callback);
      }
    };
  }

  /**
   * Subscribe to all booking updates (status changes, assignments, etc.)
   * @param {Function} callback - Callback function to handle the event
   */
  listenForBookingUpdates(callback) {
    const u1 = this.listenForEvent(EVENTS.BOOKING_UPDATED, callback);
    const u2 = this.listenForEvent(EVENTS.BOOKING_STATUS_CHANGED, callback);
    return () => {
      u1();
      u2();
    };
  }

  /**
   * Subscribe to driver assignment events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForDriverAssignment(callback) {
    return this.listenForEvent(EVENTS.DRIVER_ASSIGNED, callback);
  }

  /**
   * Subscribe to ride cancellation events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForRideCancellation(callback) {
    return this.listenForEvent(EVENTS.RIDE_CANCELLED, callback);
  }

  /**
   * Subscribe to payment and refund events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForPaymentUpdates(callback) {
    const u1 = this.listenForEvent(EVENTS.PAYMENT_RECEIVED, callback);
    const u2 = this.listenForEvent(EVENTS.REFUND_INITIATED, callback);
    const u3 = this.listenForEvent(EVENTS.REFUND_PROCESSED, callback);
    const u4 = this.listenForEvent(EVENTS.REFUND_FAILED, callback);
    return () => {
      u1();
      u2();
      u3();
      u4();
    };
  }

  /**
   * Subscribe to ride lifecycle events (started, completed)
   * @param {Function} callback - Callback function to handle the event
   */
  listenForRideLifecycle(callback) {
    const u1 = this.listenForEvent(EVENTS.RIDE_STARTED, callback);
    const u2 = this.listenForEvent(EVENTS.RIDE_COMPLETED, callback);
    return () => {
      u1();
      u2();
    };
  }

  /**
   * Subscribe to rating events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForRatingEvents(callback) {
    const u1 = this.listenForEvent(EVENTS.RATING_REQUESTED, callback);
    const u2 = this.listenForEvent(EVENTS.RATING_SUBMITTED, callback);
    return () => {
      u1();
      u2();
    };
  }

  /**
   * Subscribe to driver location updates
   * @param {Function} callback - Callback function to handle the event
   */
  listenForDriverLocationUpdates(callback) {
    return this.listenForEvent(EVENTS.DRIVER_LOCATION_UPDATED, callback);
  }

  /**
   * Subscribe to new ride requests (auto-assign)
   * @param {Function} callback - Callback function to handle the event
   */
  listenForNewRideRequests(callback) {
    return this.listenForEvent(EVENTS.NEW_RIDE_REQUEST, callback);
  }

  /**
   * Subscribe to ride taken notifications (when another driver accepts)
   * @param {Function} callback - Callback function to handle the event
   */
  listenForRideTaken(callback) {
    return this.listenForEvent(EVENTS.RIDE_TAKEN, callback);
  }

  /**
   * Subscribe to auto-assign expiration
   * @param {Function} callback - Callback function to handle the event
   */
  listenForAutoAssignExpired(callback) {
    return this.listenForEvent(EVENTS.AUTO_ASSIGN_EXPIRED, callback);
  }

  // Legacy methods for backward compatibility
  onDriverAssigned(callback) {
    return this.listenForDriverAssignment(callback);
  }

  onNewRideRequest(callback) {
    return this.listenForNewRideRequests(callback);
  }

  onRideTaken(callback) {
    return this.listenForRideTaken(callback);
  }

  onAutoAssignExpired(callback) {
    return this.listenForAutoAssignExpired(callback);
  }

  onDriverRemoved(callback) {
    return this.listenForEvent(EVENTS.DRIVER_REMOVED, callback);
  }

  onBookingUnassigned(callback) {
    return this.listenForEvent(EVENTS.BOOKING_UNASSIGNED, callback);
  }

  onBookingUpdated(callback) {
    return this.listenForEvent(EVENTS.BOOKING_UPDATED, callback);
  }

  onBookingStatusChanged(callback) {
    return this.listenForEvent(EVENTS.BOOKING_STATUS_CHANGED, callback);
  }

  onBookingCreated(callback) {
    return this.listenForEvent(EVENTS.BOOKING_CREATED, callback);
  }

  onRideCancelled(callback) {
    return this.listenForRideCancellation(callback);
  }

  onRefundProcessed(callback) {
    return this.listenForEvent(EVENTS.REFUND_PROCESSED, callback);
  }

  onRideCompleted(callback) {
    return this.listenForEvent(EVENTS.RIDE_COMPLETED, callback);
  }

  onRideStarted(callback) {
    return this.listenForEvent(EVENTS.RIDE_STARTED, callback);
  }

  onRatingSubmitted(callback) {
    return this.listenForEvent(EVENTS.RATING_SUBMITTED, callback);
  }

  onDriverRatingUpdate(callback) {
    return this.listenForEvent(EVENTS.DRIVER_RATING_UPDATE, callback);
  }

  // Expose the raw socket instance (for direct emit use-cases)
  getSocket() {
    return this.socket;
  }

  // Join a ride room for live tracking
  joinRideRoom(rideId) {
    this._joinedRooms.ride = rideId;
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinRideRoom called but socket is not connected. Saved room for auto-rejoin.', { rideId });
      return;
    }
    this.socket.emit(EVENTS.JOIN_RIDE_ROOM, rideId);
    console.log('SocketService: emitting joinRideRoom', { rideId });
  }

  // Emit a live location update (driver or passenger → server → trackers)
  // type: 'driver' | 'passenger'  (defaults to 'driver')
  emitLocationUpdate(rideId, lat, lng, type = 'driver') {
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: emitLocationUpdate called but socket is not connected');
      return;
    }
    this.socket.emit(EVENTS.LOCATION_UPDATE, { rideId, lat, lng, type });
  }

  // Driver approaching pickup (status = assigned, before ride starts)
  // userId = the passenger's user._id so server routes to their room too
  emitDriverApproaching(rideId, userId, lat, lng) {
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: emitDriverApproaching called but socket is not connected');
      return;
    }
    this.socket.emit(EVENTS.DRIVER_APPROACHING, { rideId, userId, lat, lng });
  }

  // Passenger app: listen for driver approaching (status = assigned)
  listenForDriverApproaching(callback) {
    this.listenForEvent(EVENTS.DRIVER_APPROACHING, callback);
  }

  // Disconnect the socket safely
  disconnect() {
    if (this.socket) {
      try {
        if (this.socket.connected) {
          console.log('SocketService: disconnecting socket', { id: this.socket.id });
          this.socket.disconnect();
        } else {
          console.log('SocketService: cleaning up listeners for socket (not connected)');
          this.socket.removeAllListeners();
        }
      } catch (error) {
        console.error('SocketService: error during disconnect', error);
      } finally {
        // Always clean up resources regardless of disconnection success
        this.socket = null;
        this.isConnected = false;
        this._isConnecting = false;
        console.log('SocketService: socket cleaned up');
      }
    } else {
      console.log('SocketService: disconnect called but no socket instance exists');
    }
  }
}

// Create a singleton instance
const socketService = new SocketService();

export default socketService;
